import React, { useRef, useEffect, useState, useMemo } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { useShallow } from "zustand/shallow";
import { loadModel } from "../loaders";
import gsap from "gsap";
import { AxisRuler3D } from "../utils/AxisRuler";
import { scaleModelToCentimeters } from "../utils/ModelScaler";

import useEditorStore from "../stores/useEditorStore";
import usePreparingScanStore from "../stores/usePreparingScanStore";

import { pickAnywhereOnCanvas } from "./tools/helpers";
import { ToolHandler } from "./ToolHandler";
import { TOOLS_CONFIG } from "../constants/tools";
import LoadingOverlay from "./LoadingOverlay";

const ghostModeIcon = "/other_icons/ghost_full.svg";
const colorModeIcon = "/other_icons/contrast.svg";

export const ThreeScene = ({ children }) => {
  const mountRef = useRef(null);
  const [canvas, setCanvas] = useState(null);
  
  // Get state and setters from the Zustand store
  const setSceneRefs = useEditorStore((state) => state.setSceneRefs);
  const sceneRefs = useEditorStore((state) => state.sceneRefs);
  const cameraPosition = useEditorStore((state) => state.cameraPosition);
  const activeTool = useEditorStore((state) => state.activeTool);
  const isLoading = useEditorStore((state) => state.isLoading);
  const loadingMessage = useEditorStore((state) => state.loadingMessage);
  const setIsLoading = useEditorStore((state) => state.setIsLoading);
  const uploadedModelData = useEditorStore((state) => state.uploadedModelData);
  const modelScaleUnit = useEditorStore((state) => state.modelScaleUnit);
  const modelManualScale = useEditorStore((state) => state.modelManualScale);
  const setModelScaleInfo = useEditorStore((state) => state.setModelScaleInfo);

  const [ghostModeEnabled, colorModeEnabled, toggleGhostMode, toggleColorMode] = useEditorStore(
    useShallow((state) => [
      state.ghostModeEnabled,
      state.colorModeEnabled,
      state.toggleGhostMode,
      state.toggleColorMode,
    ])
  );

  const [addPoint, selectedRegion] = usePreparingScanStore(
    useShallow((state) => [state.addPoint, state.selectedRegion])
  );

  // Effect to set up the scene (runs only once)
  useEffect(() => {
    console.log(`setting up threejs scene`);
    // This effect sets up the entire Three.js scene once
    const width = mountRef.current.clientWidth;
    const height = mountRef.current.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xeeeeee);

    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.set(0, 0, 30);
    // const camera = new THREE.OrthographicCamera( width / - 2, width / 2, height / 2, height / - 2, 1, 1000 );
    // camera.position.set(0, 0, 10);

    const renderer = new THREE.WebGLRenderer({ antialias: true, outputColorSpace: THREE.SRGBColorSpace });
    renderer.toneMapping = THREE.NoToneMapping;
    renderer.setSize(width, height);
    mountRef.current.appendChild(renderer.domElement);

    setCanvas(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;

    // Configure mouse buttons - disable right-click by default
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.ROTATE // Use rotate so Shift flips it to PAN
    };

    // Filter pointerdown so:
    // - Right without Shift is blocked (prevents right-rotate)
    // - Shift + Left is blocked (prevents shift-left pan)
    const handlePointerDownCapture = (e) => {
      const at = useEditorStore.getState().activeTool;
      const isPaintingToolActive = (
        at === TOOLS_CONFIG.MAIN_PROCESS.RECTIFICATION.main_rectification_smoothning.id ||
        at === TOOLS_CONFIG.MAIN_PROCESS.RECTIFICATION.main_rectification_blocking_zone.id ||
        at === TOOLS_CONFIG.MAIN_PROCESS.FLARES.main_flares_smoothning.id
      );

      const isRightWithoutShift = e.button === 2 && !e.shiftKey;
      const isShiftLeft = e.button === 0 && e.shiftKey;

      if (isRightWithoutShift || (isShiftLeft && !isPaintingToolActive)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const preventContextMenu = (e) => {
      e.preventDefault();
    };

    renderer.domElement.addEventListener('pointerdown', handlePointerDownCapture, true);
    renderer.domElement.addEventListener('contextmenu', preventContextMenu);

    //Create origin sphere
    const originSphere = new THREE.Mesh(
      new THREE.SphereGeometry(0.25, 32, 32),
      new THREE.MeshBasicMaterial({ color: 0xaaaaaa, depthTest: false, depthWrite: false, transparent: true, opacity: 0.5 })
    );
    originSphere.position.set(0, 0, 0);
    // scene.add(originSphere);

    // Add lights
    scene.add(new THREE.AmbientLight(0xffffff, 2));

    const directionalLight = new THREE.DirectionalLight(0xffffff, 3);
    directionalLight.position.set(5, 5, 5);
    scene.add(directionalLight);

    const directionLight2 = new THREE.DirectionalLight(0xffffff, 3);
    directionLight2.position.set(-5, -5, -5);
    scene.add(directionLight2);

    // Set refs only in the Zustand store (scene ready but no model yet)
    setSceneRefs({ scene, camera, renderer, controls, shadedMesh: null, material: null, unmodifiedMesh: null, originalCamera: camera });

    // Handle window resize
    const handleResize = () => {
      if (mountRef.current) {
        const newWidth = mountRef.current.clientWidth;
        const newHeight = mountRef.current.clientHeight;

        camera.aspect = newWidth / newHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(newWidth, newHeight);

        // Update debug edge visualization resolution if enabled
        if (import.meta.env.VITE_DEBUG_THREE === 'true') {
          import('../utils/debugMeshEdges.js').then(({ updateEdgeVisualizationResolution }) => {
            updateEdgeVisualizationResolution(scene, renderer)
          })
        }
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(mountRef.current);

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);

      const { camera: cam, controls: ctrl } = useEditorStore.getState().sceneRefs;
      if(!cam || !ctrl) return;
      ctrl.update();
      renderer.render(scene, cam);
    };
    animate();

    return () => {
      // Clean up listeners
      renderer.domElement.removeEventListener('pointerdown', handlePointerDownCapture, true);
      renderer.domElement.removeEventListener('contextmenu', preventContextMenu);

      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();

      resizeObserver.unobserve(renderer.domElement);
      resizeObserver.disconnect();

      console.log(`cleaning up threejs scene`);
    };
  }, [setSceneRefs]); // Only run once on mount

  // Separate effect to handle model loading/switching
  useEffect(() => {
    const sceneRefsState = useEditorStore.getState().sceneRefs;
    if (!sceneRefsState || !sceneRefsState.scene) return;

    // Determine which model source to use
    // Priority: uploaded data > default PLY model
    const modelSource = uploadedModelData || '/models/AK020180_Scan (2).ply';

    console.log('Loading model from source:', modelSource);

    // Show loading overlay immediately
    setIsLoading(true, 'Loading 3D model...');

    // Remove old meshes from scene
    if (sceneRefsState.shadedMesh) {
      sceneRefsState.scene.remove(sceneRefsState.shadedMesh);
      sceneRefsState.shadedMesh.geometry?.dispose();
      sceneRefsState.shadedMesh.material?.dispose();
    }
    if (sceneRefsState.unmodifiedMesh) {
      sceneRefsState.scene.remove(sceneRefsState.unmodifiedMesh);
      sceneRefsState.unmodifiedMesh.geometry?.dispose();
      sceneRefsState.unmodifiedMesh.material?.dispose();
    }

    // Defer the heavy model loading work to the next idle time to prevent UI freeze
    const loadModelAsync = () => {
      loadModel(modelSource)
        .then((mesh) => {
          console.log('📦 Model loaded successfully');

          // Apply scaling to ensure 1cm = 1 unit in Three.js scene
          const scaleInfo = scaleModelToCentimeters(
            mesh,
            modelScaleUnit === 'auto' ? null : modelScaleUnit,
            modelManualScale
          );

          // Store scale information for debug UI
          setModelScaleInfo(scaleInfo);

          console.log('✅ Model scaled and centered');
          console.log('   Dimensions (cm):', scaleInfo.dimensions);

          sceneRefsState.scene.add(mesh);

          const unmodifiedMesh = mesh.clone();
          const overlayMaterial = new THREE.MeshMatcapMaterial({
            vertexColors: false,
            color: '#FFA500',
            transparent: true,
            opacity: 0.5,
            side: THREE.DoubleSide,
            depthTest: false,
            depthWrite: false,
            polygonOffset: true,
            polygonOffsetFactor: -0.1,
          });
          unmodifiedMesh.material = overlayMaterial;
          sceneRefsState.scene.add(unmodifiedMesh);
          unmodifiedMesh.visible = false;
          unmodifiedMesh.renderOrder = 5e5;

          // Create normal material for debugging
          const normalMaterial = new THREE.MeshNormalMaterial({ side: THREE.DoubleSide });

          // Debug edge visualization (only if VITE_DEBUG_THREE is enabled)
          if (import.meta.env.VITE_DEBUG_THREE === 'true') {
            import('../utils/debugMeshEdges.js').then(({ createEdgeVisualization }) => {
              console.log('🔍 DEBUG MODE: Rendering mesh edges...')
              console.log('Scene before adding debug edges:', {
                children: sceneRefsState.scene.children.length,
                renderer: !!sceneRefsState.renderer
              })

              const debugGroup = createEdgeVisualization(mesh.geometry, sceneRefsState.scene, {
                showManifold: false,   // Hide good edges (too many, clutters view)
                showBoundary: true,    // Show boundary edges (YELLOW = holes)
                showNonManifold: true, // Show non-manifold edges (RED = bad topology)
                lineWidth: 10,         // Increased line width for visibility
                depthTest: false       // Render on top of mesh
              }, sceneRefsState.renderer)

              console.log('Debug group created:', debugGroup)
              console.log('Scene after adding debug edges:', {
                children: sceneRefsState.scene.children.length
              })
            })
          }

          // Update refs with new meshes
          setSceneRefs({ ...sceneRefsState, shadedMesh: mesh, material: mesh.material, unmodifiedMesh, normalMaterial });

          // Hide loading overlay when done
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Error loading model:", err);
          // Hide loading overlay even on error
          setIsLoading(false);
        });
    };

    // Use requestIdleCallback to defer the model loading until browser is idle
    // Falls back to setTimeout if requestIdleCallback is not available
    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadModelAsync, { timeout: 2000 });
    } else {
      setTimeout(loadModelAsync, 0);
    }
  }, [uploadedModelData, modelScaleUnit, modelManualScale, setSceneRefs, setModelScaleInfo, setIsLoading]);

  // This effect handles camera animations when cameraPosition changes
  useEffect(() => {
    if(!sceneRefs || !cameraPosition) return;
    const { camera } = sceneRefs;

    gsap.killTweensOf(camera.position);

    gsap.to(camera.position, {
      x: cameraPosition.x,
      y: cameraPosition.y,
      z: cameraPosition.z,
      duration: 0.5,
      ease: "power3.out",
      onUpdate: () => {
        camera.lookAt(0, 0, 0);
      }
    });

  }, [cameraPosition]);

  const mouseDragThreshold = 5; // Max pixels to move to be considered a click
  const dragStartPos = useRef(null);

  const handleMouseDown = (e) => {
    // handleCursorDown(e, sceneRefs);
    // if(e.target !== canvas) return;

    if (e.button !== 0) return;
    dragStartPos.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = (e) => {
    // handleCursorUp(e, sceneRefs);

    if (e.button !== 0 || !dragStartPos.current || e.target !== canvas) return;

    const startPos = dragStartPos.current;
    const endPos = { x: e.clientX, y: e.clientY };
    dragStartPos.current = null; // Reset for the next click

    // Calculate the distance the mouse moved
    const distance = Math.hypot(endPos.x - startPos.x, endPos.y - startPos.y);

    // If the mouse didn't move much, it's a click
    if (distance < mouseDragThreshold) {
      if (activeTool === TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.TRIM.id) {
        updateScanPreparingScissors(e);
      }
    }
  };

  const updateScanPreparingScissors = (e) => {
    if(selectedRegion?.size > 0) return; // Don't add points if a region is already selected

    const point = pickAnywhereOnCanvas(e, sceneRefs);
    addPoint(point);
  };

  const modeButtons = useMemo(
    () => [
      {
        key: "ghost",
        label: "Ghost",
        icon: ghostModeIcon,
        active: ghostModeEnabled,
        onClick: toggleGhostMode,
      },
      {
        key: "color",
        label: "Color",
        icon: colorModeIcon,
        active: colorModeEnabled,
        onClick: toggleColorMode,
      },
    ],
    [ghostModeEnabled, colorModeEnabled, toggleGhostMode, toggleColorMode]
  );

  return (
    <div ref={mountRef} className="relative w-full h-full flex items-center" onMouseDown={handleMouseDown} onMouseUp={handleMouseUp}>
      {children}
      <ModeTogglePanel buttons={modeButtons} />
      {isLoading && <LoadingOverlay message={loadingMessage} />}
      <ToolHandler/>
    </div>
  );
};

const ModeTogglePanel = React.memo(({ buttons }) => {
  return (
    <div className="pointer-events-none absolute right-4 top-1/2 z-40 -translate-y-1/2">
      <div className="pointer-events-auto flex flex-col gap-1.5 rounded-2xl border border-white/40 bg-white/60 p-1.5 shadow-[0_6px_16px_rgba(15,23,42,0.12)] backdrop-blur-md">
        {buttons.map(({ key, label, icon, active, onClick }) => (
          <ModeToggleButton key={key} label={label} icon={icon} active={active} onClick={onClick} />
        ))}
      </div>
    </div>
  );
});

const ModeToggleButton = ({ icon, label, active, onClick }) => {
  const activeClasses = "bg-[#d8c8ff] border-[#b7a4ff] text-[#312e81] shadow-[0_8px_16px_rgba(167,139,250,0.28)]";
  const inactiveClasses =
    "bg-white/90 border-white/40 text-slate-600 hover:bg-white hover:border-[#c4b5fd] hover:text-slate-800";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={`${label} mode ${active ? "enabled" : "disabled"}`}
      className={`group relative flex w-14 flex-col items-center gap-1.5 rounded-xl border px-1.5 py-2 text-[0.58rem] font-semibold uppercase tracking-wide transition-all duration-200 ${active ? activeClasses : inactiveClasses} cursor-pointer`}
    >
      <span
        className={`absolute top-1 right-1 h-2 w-2 rounded-full transition-opacity ${active ? "bg-white opacity-80" : "bg-slate-400 opacity-0 group-hover:opacity-60"}`}
      />
      <div
        className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-all duration-200 ${
          active
            ? "border-white/40 bg-white/40"
            : "border-white/50 bg-white/80 group-hover:border-[#d8c8ff] group-hover:bg-white"
        }`}
      >
        <img
          src={icon}
          alt=""
          className={`h-5 w-5 select-none transition-transform duration-200 ${
            active ? "opacity-100" : "opacity-70 group-hover:opacity-100 group-hover:scale-105"
          }`}
        />
      </div>
      <span className="sr-only">{label} mode toggle</span>
      <span className={`text-[0.6rem] font-medium tracking-wide ${active ? "text-[#312e81]" : "text-slate-600 group-hover:text-slate-800"}`}>
        {label}
      </span>
    </button>
  );
};